import { ChangeDetectionStrategy, Component, inject, signal, ViewChild } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { FileMetadata } from '@shared/services/api.service';
import { EntityType, EventType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FileManagerComponent, FileViewerComponent],
  selector: 'aux-specifications-upload',
  template: `
    <div class="w-screen max-w-[280px]"></div>
    <aux-file-manager
      #manager
      class="h-32"
      [fetchFilesOnInit]="false"
      [pathFn]="pathFn"
      [eager]="false"
      [metadata]="metadata"
      [showSuccessOnUpload]="true"
      [accept]="'.csv'"
    />
    <div class="mt-4 max-h-60 overflow-auto">
      <aux-file-viewer
        [fileManager]="manager"
        [disableFirstFileMargin]="true"
        [onlyShowUploaded]="false"
      />
    </div>

    @if (errorMessage()) {
      <div class="mt-4 rounded-md bg-aux-error p-5 font-medium text-white">
        {{ errorMessage() }}
      </div>
    }
  `,
})
export class SpecificationsUploadComponent {
  readonly ref = inject(CustomOverlayRef<unknown, unknown>);

  private readonly eventService = inject(EventService);
  private readonly mainQuery = inject(MainQuery);
  private readonly overlayService = inject(OverlayService);

  errorMessage = signal('');
  loading = signal(false);

  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  metadata: FileMetadata = {};

  getFilePath() {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/specification/`;
  }

  async onUpload() {
    this.errorMessage.set('');

    if (this.fileManager && !this.loading()) {
      const files = this.fileManager.fileQuery.getAll();

      if (!files.length) {
        this.errorMessage.set(MessagesConstants.FILE.NEED_UPLOAD_FILE);

        return;
      }

      if (files.length > 1) {
        this.errorMessage.set(MessagesConstants.FILE.MAX_ONE_FILE);

        return;
      }

      const match = files[0].bucket_key.match(/\.([^.]+)$/);

      if (!(match?.[1] === 'csv' || match?.[1] === 'png')) {
        this.errorMessage.set('File type must be .csv or .png!');

        return;
      }

      this.loading.set(true);

      const file = files[0];
      const key = `${this.getFilePath()}${file.bucket_key}`;

      this.fileManager.fileStore.update(file.id, {
        ...file,
        bucket_key: key,
      });

      const fileSuccess = await this.fileManager.fileService.uploadFiles({
        metadata: { admin: '1' },
      });

      if (fileSuccess) {
        const mode = match?.[1] === 'csv' ? 'csv' : 'png';

        const { errors, success } = await firstValueFrom(
          this.eventService.processEvent$({
            bucket_key: `public/${key}`,
            entity_id: this.mainQuery.getValue().trialKey,
            entity_type: EntityType.TRIAL,
            type:
              mode === 'csv'
                ? EventType.SPECIFICATIONS_TEMPLATE_UPLOADED
                : EventType.SPECIFICATIONS_IMAGE_UPLOADED,
          })
        );

        if (success) {
          this.overlayService.success(`Specification successfully uploaded!`);
        } else {
          void this.fileManager.apiService.removeFile(`${this.getFilePath()}${file.bucket_key}`);
          this.overlayService.error(errors);
        }

        this.ref.close(true);
      }
    }

    this.loading.set(false);
  }

  pathFn: () => string = () => '';
}
